import React from 'react'

import { EChartsOption } from 'echarts'
import { BarChart, BarSeriesOption } from 'echarts/charts'
import {
  TitleComponent,
  TitleComponentOption,
  TooltipComponent,
  TooltipComponentOption,
  GridComponent,
  GridComponentOption,
  LegendComponent,
  LegendComponentOption,
  DatasetComponent,
} from 'echarts/components'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import ReactECharts from 'echarts-for-react'

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  CanvasRenderer,
  DatasetComponent,
])

export interface HChartSeries extends Partial<BarSeriesOption> {
  name: string
  data: number[] | string[]
}

export interface HorizontalBarChartProps {
  // title: string
  subTitle?: string
  yAxisData: string[]
  series: HChartSeries[]
  className?: string
  showLegend?: boolean | 'auto'
  onBarClick?: (params: { seriesName: string; name: string }) => void
  xAxisFormatter?: (value: number) => string
}

type ECOption =
  | EChartsOption
  | {
      title?: TitleComponentOption
      tooltip?: TooltipComponentOption
      grid?: GridComponentOption
      legend?: LegendComponentOption
      series?: BarSeriesOption | BarSeriesOption[]
    }

export default function HorizontalBarChart({
  series,
  yAxisData,
  showLegend,
  onBarClick,
  xAxisFormatter,
}: HorizontalBarChartProps) {
  const processedSeries: BarSeriesOption[] = series.map((s) => ({
    type: 'bar' as const,
    barWidth: '50%',
    // barMinHeight: '100',
    // barMinWidth: '20',
    ...s,
  }))

  let legendVisible: boolean
  if (showLegend === 'auto') {
    legendVisible = processedSeries.length > 1
  } else {
    legendVisible = showLegend ?? false
  }

  const option: ECOption = {
    // title: {
    //   text: title,
    //   subtext: subTitle,
    // },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,

      axisPointer: {
        type: 'shadow',
      },
      order: 'seriesAsc',
    },
    legend: {
      show: legendVisible,
      bottom: 5,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: legendVisible ? '10%' : '0%',
      top: '10',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'value',
        // Add axis label formatting
        axisLabel: {
          formatter:
            xAxisFormatter ||
            ((value: number) => {
              // Default formatter with thousand separators
              if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M'
              if (value >= 1000) return (value / 1000).toFixed(0) + 'K'
              return value.toString()
            }),
          // Prevent label overlap
          rotate: 0,
          margin: 8,
        },
        // Control split lines for better readability
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            opacity: 0.3,
          },
        },
      },
    ],
    yAxis: [
      {
        type: 'category',
        data: yAxisData,
        axisTick: {
          alignWithLabel: true,
        },
      },
    ],

    series: processedSeries,
  }
  const onEvents = onBarClick
    ? {
        click: (params: { seriesName: string; name: string }) => {
          onBarClick(params)
        },
      }
    : undefined
  return <ReactECharts option={option} style={{ height: '95%', width: '98%' }} onEvents={onEvents} />
}
